import { defineReference, group, item, topic } from "../define";

/* Groups and methods of the Subbly.js SDK reference; see ../define.ts. */

export const subblySdk = defineReference({
  path: "/reference/subbly-sdk",
  title: "Subbly.js SDK",
  description:
    "Methods of the Subbly.js SDK, Subbly's browser-side JavaScript library.",
  // Sections of the overview page, in the order they appear there.
  topics: [
    topic("Cookies"),
    topic("Amounts and dates"),
    topic("Currency"),
    topic("Expand parameters"),
    topic("Errors"),
  ],
  groups: [
    group(
      "setup",
      "Setup",
      "Start Subbly.js with your Storefront API key, then set the currency and the language.",
      [
        item(
          "init",
          "Start the SDK",
          "Subbly.init(config)",
          "Starts the SDK with your Storefront API key and resolves with an SDK instance.",
        ),
        item(
          "set-currency",
          "Set the currency",
          "subbly.setCurrency(currencyCode)",
          "Sets the currency used for prices in later product, bundle, and survey responses.",
        ),
        item(
          "set-language",
          "Set the language",
          "subbly.setLanguage(languageCode)",
          "Sets the language sent with every later request.",
        ),
        item(
          "version",
          "Version",
          "subbly.VERSION",
          "The version of the SDK package.",
        ),
        item(
          "config",
          "Configuration",
          "subbly.config",
          "The API key, the API URL, and the language the SDK started with.",
        ),
      ],
    ),
    group(
      "addresses",
      "Addresses",
      "Saved shipping and billing addresses of the signed-in customer.",
      [
        item(
          "list",
          "List the addresses",
          "subbly.addresses.list()",
          "Gets every saved address of the signed-in customer.",
        ),
        item(
          "store",
          "Add an address",
          "subbly.addresses.store(payload)",
          "Creates a new saved address for the signed-in customer.",
        ),
        item(
          "delete",
          "Delete an address",
          "subbly.addresses.delete(addressId)",
          "Deletes a saved address.",
        ),
      ],
    ),
    group(
      "auth",
      "Authentication",
      "Sign a customer in, register a new one, and hold the access token.",
      [
        item(
          "is-authenticated",
          "Authentication state",
          "subbly.auth.isAuthenticated",
          "Tells you whether the SDK holds a working access token.",
        ),
        item(
          "check-authenticated",
          "Check the authentication",
          "subbly.auth.checkAuthenticated(token?)",
          "Verifies the stored access token, and clears it when it no longer works.",
        ),
        item(
          "login",
          "Log in with a password",
          "subbly.auth.login(payload)",
          "Signs the customer in with an email and a password.",
        ),
        item(
          "register",
          "Register a customer",
          "subbly.auth.register(payload)",
          "Creates an account and signs the new customer in.",
        ),
        item(
          "registered",
          "Check if an email is registered",
          "subbly.auth.registered(payload)",
          "Reports whether an email already has a Subbly account.",
        ),
        item(
          "otp",
          "Send a one-time password",
          "subbly.auth.otp(payload)",
          "Emails a one-time password to the address you give.",
        ),
        item(
          "otp-login",
          "Log in with a one-time password",
          "subbly.auth.otpLogin(payload)",
          "Signs the customer in with the one-time password sent to their email.",
        ),
        item(
          "social",
          "Log in with single sign-on",
          "subbly.auth.social(payload)",
          "Signs the customer in with a Google or Facebook token.",
        ),
        item(
          "logout",
          "Log out",
          "subbly.auth.logout()",
          "Clears the access token the SDK holds.",
        ),
        item(
          "get-access-token",
          "Get the access token",
          "subbly.auth.getAccessToken()",
          "Reads the access token from the browser cookie.",
        ),
      ],
    ),
    group(
      "bundles",
      "Bundles",
      "Read bundles, their items and groups, and price the items a customer picked.",
      [
        item(
          "list",
          "List the bundles",
          "subbly.bundles.list(params, headers?)",
          "Gets a paginated list of the shop's bundles.",
        ),
        item(
          "load",
          "Load a bundle",
          "subbly.bundles.load(bundleId, params?, headers?)",
          "Gets one bundle with its plans, rulesets, filters, and preferences.",
        ),
        item(
          "load-items",
          "Load the bundle items",
          "subbly.bundles.loadItems(bundleId, params?, headers?)",
          "Gets the products a customer can pick for the bundle.",
        ),
        item(
          "load-groups",
          "Load the bundle groups",
          "subbly.bundles.loadGroups(bundleId, params?, headers?)",
          "Gets the item groups of a bundle, each with its own quantity limits.",
        ),
        item(
          "quote",
          "Quote a bundle",
          "subbly.bundles.quote(bundleId, payload, params?, headers?)",
          "Prices a bundle for the exact items and preferences a customer picked.",
        ),
      ],
    ),
    group(
      "cart",
      "Cart",
      "Create and change the cart, and read the delivery options and dates for it.",
      [
        item(
          "create",
          "Create a cart",
          "subbly.cart.create(payload?, params?)",
          "Creates a new cart and stores its ID in a browser cookie.",
        ),
        item(
          "load",
          "Load a cart",
          "subbly.cart.load(cartId?, params?)",
          "Loads a cart by ID into subbly.cart.",
        ),
        item(
          "update",
          "Update the cart",
          "subbly.cart.update(payload, params?)",
          "Changes the customer details, addresses, delivery, and discounts on the cart.",
        ),
        item(
          "add-item",
          "Add an item",
          "subbly.cart.addItem(payload, params?)",
          "Adds a product, a plan, or a bundle to the cart.",
        ),
        item(
          "update-item",
          "Update an item",
          "subbly.cart.updateItem(cartItemId, payload, params?)",
          "Changes the quantity, the answers, or the bundle contents of one item.",
        ),
        item(
          "remove-item",
          "Remove an item",
          "subbly.cart.removeItem(cartItemId, params?)",
          "Removes one item from the cart.",
        ),
        item(
          "get-start-dates",
          "Get the start dates",
          "subbly.cart.getStartDates()",
          "Gets the dates on which the subscription in the cart can start.",
        ),
        item(
          "get-gifting-dates",
          "Get the gifting dates",
          "subbly.cart.getGiftingDates()",
          "Gets the dates on which a gift subscription in the cart can start.",
        ),
        item(
          "get-shipping-methods",
          "Get the shipping methods",
          "subbly.cart.getShippingMethods(params)",
          "Gets the delivery options available for the cart at a destination.",
        ),
        item(
          "get-local-pickups",
          "Get the pick-up points",
          "subbly.cart.getLocalPickups()",
          "Gets the pick-up points available for the cart.",
        ),
        item(
          "attach-customer",
          "Attach the customer",
          "subbly.cart.attachCustomer(params?)",
          "Assigns the cart to the signed-in customer.",
        ),
      ],
    ),
    group("checkout", "Checkout", "Pay for the cart.", [
      item(
        "purchase",
        "Purchase the cart",
        "subbly.checkout.purchase(cartId, payload?, params?)",
        "Charges the cart and creates the subscription, the invoice, and the orders.",
      ),
    ]),
    group(
      "countries",
      "Countries",
      "The country and region list the shop supports.",
      [
        item(
          "list",
          "List the countries",
          "subbly.countries.list()",
          "Gets every country Subbly supports, with its regions.",
        ),
      ],
    ),
    group(
      "customers",
      "Customers",
      "Read and change the profile of the signed-in customer.",
      [
        item(
          "me",
          "Get the customer profile",
          "subbly.customers.me()",
          "Gets the profile of the signed-in customer.",
        ),
        item(
          "update",
          "Update the customer",
          "subbly.customers.update(payload)",
          "Changes the profile of the signed-in customer.",
        ),
        item(
          "referral",
          "Get the referral link",
          "subbly.customers.referral()",
          "Gets the referral link of the signed-in customer.",
        ),
      ],
    ),
    group(
      "funnels",
      "Funnels",
      "Offers before, during, and after the purchase: get the next one, accept it, or skip it.",
      [
        item(
          "pre-purchase-fetch",
          "Get a pre-purchase offer",
          "subbly.funnels.prePurchaseFetch(cartId, params?)",
          "Gets the next pre-purchase offer for the cart.",
        ),
        item(
          "pre-purchase-accept",
          "Accept a pre-purchase offer",
          "subbly.funnels.prePurchaseAccept(cartId, funnelStepId, payload, params?)",
          "Accepts a pre-purchase offer and adds its products to the cart.",
        ),
        item(
          "pre-purchase-reject",
          "Skip a pre-purchase offer",
          "subbly.funnels.prePurchaseReject(cartId, funnelStepId)",
          "Skips a pre-purchase offer so the next one can be fetched.",
        ),
        item(
          "pre-purchase-refresh",
          "Reset the pre-purchase offers",
          "subbly.funnels.prePurchaseRefresh(cartId)",
          "Clears which pre-purchase offers the customer accepted or skipped.",
        ),
        item(
          "mid-purchase-fetch",
          "Get a mid-purchase offer",
          "subbly.funnels.midPurchaseFetch(cartId, params?)",
          "Gets the next mid-purchase offer for the cart.",
        ),
        item(
          "mid-purchase-accept",
          "Accept a mid-purchase offer",
          "subbly.funnels.midPurchaseAccept(cartId, funnelStepId, payload, params?)",
          "Accepts a mid-purchase offer and adds its products to the cart.",
        ),
        item(
          "mid-purchase-reject",
          "Skip a mid-purchase offer",
          "subbly.funnels.midPurchaseReject(cartId, funnelStepId)",
          "Skips a mid-purchase offer so the next one can be fetched.",
        ),
        item(
          "post-purchase-fetch",
          "Get a post-purchase offer",
          "subbly.funnels.postPurchaseFetch(cartId, params?)",
          "Gets the next post-purchase offer, after the cart is paid.",
        ),
        item(
          "post-purchase-accept",
          "Accept a post-purchase offer",
          "subbly.funnels.postPurchaseAccept(cartId, funnelStepId, payload, params?)",
          "Accepts a post-purchase offer and charges it as a new purchase.",
        ),
        item(
          "post-purchase-reject",
          "Skip a post-purchase offer",
          "subbly.funnels.postPurchaseReject(cartId, funnelStepId)",
          "Skips a post-purchase offer so the next one can be fetched.",
        ),
      ],
    ),
    group("lead", "Leads", "Save an email address as a lead before checkout.", [
      item(
        "subscribe",
        "Capture a lead",
        "subbly.lead.subscribe(payload)",
        "Saves an email address as a lead.",
      ),
    ]),
    group(
      "metafields",
      "Metafields",
      "Read the metafield definitions of the shop.",
      [
        item(
          "list",
          "List the metafields",
          "subbly.metafield.list(params)",
          "Gets a paginated list of the shop's metafield definitions.",
        ),
      ],
    ),
    group(
      "payment-intents",
      "Payment intents",
      "Finish Stripe 3-D Secure verification after a purchase asks for it.",
      [
        item(
          "get-payment-intent",
          "Get a payment intent",
          "subbly.paymentIntents.getPaymentIntent(paymentIntentId)",
          "Gets a payment intent, including the client secret for 3-D Secure.",
        ),
        item(
          "confirm",
          "Confirm a payment intent",
          "subbly.paymentIntents.confirm(paymentIntentId, externalPaymentId)",
          "Tells Subbly that the payment intent is verified.",
        ),
      ],
    ),
    group(
      "pickup-info",
      "Pick-up info",
      "Read, create, and delete the pick-up contact records of the customer.",
      [
        item(
          "list",
          "List the pick-up infos",
          "subbly.pickupInfo.list(params)",
          "Gets the pick-up contact records of the signed-in customer.",
        ),
        item(
          "store",
          "Add a pick-up info",
          "subbly.pickupInfo.store(payload)",
          "Creates a pick-up contact record for the customer.",
        ),
        item(
          "delete",
          "Delete a pick-up info",
          "subbly.pickupInfo.delete(pickupInfoId)",
          "Deletes a pick-up contact record.",
        ),
      ],
    ),
    group(
      "products",
      "Products",
      "List the products, and load a product, a variant, or a plan.",
      [
        item(
          "list",
          "List the products",
          "subbly.products.list(params?, headers?)",
          "Gets a paginated list of the shop's parent products.",
        ),
        item(
          "load",
          "Load a product",
          "subbly.products.load(productId, params?, headers?)",
          "Loads one parent product with its variants or its plans.",
        ),
        item(
          "load-variant",
          "Load a variant",
          "subbly.products.loadVariant(productId, params?, headers?)",
          "Loads one product variant.",
        ),
        item(
          "load-plan",
          "Load a plan",
          "subbly.products.loadPlan(productId, params?, headers?)",
          "Loads one subscription plan.",
        ),
      ],
    ),
    group(
      "shop",
      "Shop",
      "Settings, branding, currencies, and gateways of the shop.",
      [
        item(
          "load",
          "Load the shop",
          "subbly.shop.load()",
          "Loads the shop settings, branding, currencies, gateways, and apps.",
        ),
      ],
    ),
    group(
      "stock",
      "Stock notifications",
      "Ask Subbly to email a customer when an item is back in stock.",
      [
        item(
          "subscribe",
          "Get a back-in-stock alert",
          "subbly.stock.subscribe(payload)",
          "Asks Subbly to email the customer when a product or a survey answer is back in stock.",
        ),
      ],
    ),
    group(
      "subscriptions",
      "Subscriptions",
      "Read the customer's subscriptions, and change their quantity, bundle, and survey answers.",
      [
        item(
          "list",
          "List the subscriptions",
          "subbly.subscriptions.list(params, headers?)",
          "Gets a paginated list of the customer's subscriptions.",
        ),
        item(
          "load",
          "Load a subscription",
          "subbly.subscriptions.load(subscriptionId, params?, headers?)",
          "Loads one subscription.",
        ),
        item(
          "update",
          "Update a subscription",
          "subbly.subscriptions.update(subscriptionId, payload, params?, headers?)",
          "Changes the quantity or the metadata of a subscription.",
        ),
        item(
          "update-preferences",
          "Update the survey preferences",
          "subbly.subscriptions.updatePreferences(subscriptionId, payload, headers?)",
          "Rewrites the survey answers of a subscription.",
        ),
        item(
          "update-bundle",
          "Update the bundle",
          "subbly.subscriptions.updateBundle(subscriptionId, payload, headers?)",
          "Rewrites the bundle contents and preferences of a subscription.",
        ),
        item(
          "load-item",
          "Load a subscription item",
          "subbly.subscriptions.loadItem(subscriptionId, itemId, params?, headers?)",
          "Loads one item of a subscription.",
        ),
        item(
          "update-item",
          "Update a subscription item",
          "subbly.subscriptions.updateItem(subscriptionId, itemId, payload, params?, headers?)",
          "Changes the quantity or the metadata of one subscription item.",
        ),
        item(
          "update-item-bundle",
          "Update an item bundle",
          "subbly.subscriptions.updateItemBundle(subscriptionId, itemId, payload, params?, headers?)",
          "Rewrites the bundle contents of one subscription item.",
        ),
        item(
          "update-item-preferences",
          "Update the item survey preferences",
          "subbly.subscriptions.updateItemPreferences(subscriptionId, itemId, payload, headers?)",
          "Rewrites the survey answers of one subscription item.",
        ),
      ],
    ),
    group("surveys", "Surveys", "Load a survey with its questions and answers.", [
      item(
        "load",
        "Load a survey",
        "subbly.surveys.load(surveyId, params?, headers?)",
        "Loads a survey with its questions, answers, and styling.",
      ),
    ]),
    group(
      "wallet",
      "Wallet",
      "List the saved payment methods of the customer, and add a new one.",
      [
        item(
          "list",
          "List the payment methods",
          "subbly.wallet.list(currencyCode)",
          "Gets the payment methods the customer saved for a currency.",
        ),
        item(
          "setup",
          "Start a payment setup",
          "subbly.wallet.setup(payload)",
          "Gets the token a gateway widget needs before it collects payment details.",
        ),
        item(
          "store",
          "Add a payment method",
          "subbly.wallet.store(payload)",
          "Saves a payment source for the customer from a gateway token.",
        ),
        item(
          "setup-intent",
          "Start a 3-D Secure setup",
          "subbly.wallet.setupIntent(payload)",
          "Gets a Subbly-hosted URL for Stripe 3-D Secure card verification.",
        ),
      ],
    ),
  ],
});
